import {
  CustomParams,
  CustomParamsWithoutUserProperties,
} from '../google-analytics/ga4Types';
import { getNonEmptyValuesFromObject } from '../../utils/object';
import { ECommerceItem, ECommerceTransaction } from '../types';
import { userPropertyToCustomDimensionDictionary } from '../google-analytics/ga4Maps';
import { createConfigParametersRepository } from '../../configParametersRepository';
import { CUSTOM_PARAMS_STORAGE_KEY, STORAGE_KEY } from '../resources/constants';
import {
  getGa4FromUaEcommerceItem,
  getGa4FromUAEcommerceTransaction,
} from './mapping';
import {
  ECommerce4Item,
  ECommerce4Transaction,
  TrackECommerceArg,
} from './types';
import { getCustomParamsFromDimensions } from '../google-analytics/ga4';
import { getPageLocation } from '../google-analytics/utils';
import { createCustomDimensionsRepository } from '../customDimensionsRepository';
import { extractParametersFromPageName } from '../../configParametersRepository/storageClient/utils';

export const normalizePageName = (pageName: string) => {
  return `/${(pageName || '').replace(/^\/|\/$/g, '')}/`;
};

export const getFlowAndProductFromCurrentPageName = () => {
  const { getAllParams } = createCustomDimensionsRepository();
  const customParams = getAllParams();
  const currentPageName = customParams?.screen_name;
  if (!currentPageName) return;
  const [flowAndProduct, flow, product] = extractParametersFromPageName(
    normalizePageName(currentPageName)
  );
  if (!flow || !product) return;
  return flowAndProduct;
};

/**
 * @returns {CustomDimensions} transaction params.
 */
export const getTransactionEcommerceParams = (): CustomParams | undefined => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  const {
    app_traffic,
    number_of_passengers,
    number_of_adults,
    number_of_children,
    number_of_infants,
    duration,
    number_of_rooms_in_dp,
    days_in_advanced,
    gross_sale,
    site_type,
    lang_cod,
    market,
    arrival_date,
    departure_date,
    payment_method,
    product_code,
    sso,
    visit_id,
    mkttrack,
    mktsession_id,
    tt1,
    tt2,
    tt3,
    tt4,
    tt5,
    tt6,
    tt7,
    tt8,
    tt9,
    tt10,
    tt11,
    tt12,
    tt13,
    screen_name,
    edo_campaign_code,
    smart_funnel_productstoskip,
  } =
    storageClient.getFromStorage('sessionStorage', STORAGE_KEY)?.[
      CUSTOM_PARAMS_STORAGE_KEY
    ] || {};

  return getNonEmptyValuesFromObject<CustomParams>({
    app_traffic,
    number_of_passengers,
    number_of_adults,
    number_of_children,
    number_of_infants,
    duration,
    number_of_rooms_in_dp,
    days_in_advanced,
    gross_sale,
    site_type,
    lang_cod,
    market,
    arrival_date,
    departure_date,
    payment_method,
    product_code,
    sso,
    visit_id,
    mkttrack,
    mktsession_id,
    tt1,
    tt2,
    tt3,
    tt4,
    tt5,
    tt6,
    tt7,
    tt8,
    tt9,
    tt10,
    tt11,
    tt12,
    tt13,
    screen_name,
    edo_campaign_code,
    smart_funnel_productstoskip,
  });
};

/**
 * Checks if the data object is a GA4 E-Commerce transaction.
 *
 * @param {ECommerce4Transaction | TrackECommerceArg} data - The data object to check.
 * @returns {boolean} Returns true if the object is a GA4 E-Commerce transaction, otherwise false.
 */
export function isGa4EcommerceData(
  data: ECommerce4Transaction | TrackECommerceArg
): data is ECommerce4Transaction {
  return 'items' in data && 'transaction_id' in data;
}

/**
 * Checks if the data object is a UA E-Commerce transaction.
 *
 * @param {ECommerce4Transaction | TrackECommerceArg} data - The data object to check.
 * @returns {boolean} Returns true if the object is a UA E-Commerce transaction, otherwise false.
 */
export function isUaEcommerceData(
  data: ECommerce4Transaction | TrackECommerceArg
): data is TrackECommerceArg {
  return 'products' in data && 'transaction' in data;
}

/**
 * Filters out user properties from the given custom parameters.
 *
 * @param {CustomParams} customParams - The custom parameters to filter.
 * @returns {CustomParamsWithoutUserProperties} The filtered custom parameters.
 */
export const getCustomParamsWithoutUserProperties = (
  customParams: CustomParams
): CustomParamsWithoutUserProperties => {
  return Object.keys(customParams).reduce((acc, key) => {
    if (userPropertyToCustomDimensionDictionary[key] !== undefined) {
      return acc;
    }
    return { ...acc, [key]: customParams[key] };
  }, {});
};

/**
 * Create ecommerce (UA) from ecommerce (GA4)
 * Use only during migration
 * @returns
 * @param uaEcommerce
 */
function createGa4FromUaECommerceData(
  uaEcommerce: TrackECommerceArg
): ECommerce4Transaction {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const { getTrackingStoredValue } = getTrackingStorageClient();
  const storedParams = getTrackingStoredValue(
    CUSTOM_PARAMS_STORAGE_KEY
  ) as CustomParams;
  const ga4TransactionParamsFromStorage = getTransactionEcommerceParams();
  const transaction: ECommerceTransaction = uaEcommerce.transaction;
  const items: ECommerceItem[] = uaEcommerce.products;
  const transaction4 = getGa4FromUAEcommerceTransaction(transaction);
  transaction4.currency = 'EUR';
  if (
    transaction4.transaction_id !== null &&
    transaction4.transaction_id !== undefined
  ) {
    transaction4.transaction_id = transaction4.transaction_id.toString();
  }
  transaction4.page_location = getPageLocation(storedParams?.screen_name);
  if (transaction4.affiliation) {
    transaction4.product_code = transaction4.affiliation;
  }
  transaction4.items = (items?.map((item) => {
    let ga4Item = {
      ...getGa4FromUaEcommerceItem(item),
      // NOTE: can this be removed? getCustomParamsFromDimensions already filters out user properties
      ...getCustomParamsWithoutUserProperties(
        getCustomParamsFromDimensions(item)
      ),
    };
    if (ga4Item.payment_method) {
      const { payment_method, ...rest } = ga4Item;
      transaction4.payment_method = payment_method;
      ga4Item = rest;
    }
    if (ga4Item.arrival_date) {
      transaction4.arrival_date = ga4Item.arrival_date;
    }
    if (ga4Item.departure_date) {
      transaction4.departure_date = ga4Item.departure_date;
    }
    return {
      ...ga4Item,
    };
  }) || []) as ECommerce4Item[];

  const transaction4ParamsFromUaTransactionDimensions =
    getCustomParamsFromDimensions(transaction);

  return {
    ...transaction4,
    ...ga4TransactionParamsFromStorage,
    ...transaction4ParamsFromUaTransactionDimensions,
  };
}

/**
 * Return GA4 ecommerce data and UA ecommerce data
 * Use only during migration
 * @returns
 * @param eCommerceData
 */
export function getEcommerceData(
  eCommerceData: TrackECommerceArg | ECommerce4Transaction
) {
  let ga4Data = { ...eCommerceData } as ECommerce4Transaction;
  if (isGa4EcommerceData(eCommerceData)) {
    ga4Data = { ...ga4Data, ...getTransactionEcommerceParams() };
  } else if (isUaEcommerceData(eCommerceData)) {
    ga4Data = createGa4FromUaECommerceData(eCommerceData);
  }
  return { ga4Data };
}
